import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '../../../environments/environment';
import { RestService } from '../rest/rest.service';
import { Store } from '@ngrx/store';
import * as fromApp from '../../app.reducer';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SocketService } from '../socket/socket.service';
import { User, UserService } from '../user/user.service';
import { PushService } from '../push/push.service';
import { ToastService } from '../toast/toast.service';
import { AppLifecycleService } from '../app-lifecycle/app-lifecycle.service';
import { Router } from '@angular/router';

@Injectable({
	providedIn: 'root',
})
export class ChatService implements OnDestroy {
	public items: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
	public archivedItems: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
	public messages: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
	public typingIndicatorDelay = 1500;
	
	/** Emits true when all thread list pages have been loaded (no more pagination) */
	private threadListCompleteSubject = new BehaviorSubject<boolean>(false);
	public threadListComplete$ = this.threadListCompleteSubject.asObservable();

	private _totalItems: any[] = [];
	private _archivedItems: any[] = [];
	private _user: User;
	private _subscriptions: Subscription[] = [];

	private unreadCountSubject = new BehaviorSubject<number>(0);
	public unreadCount$ = this.unreadCountSubject.asObservable();
	private activeThreadSubject = new BehaviorSubject<string | null>(null);

	/** Guards against overlapping resume refreshes if resume fires in bursts. */
	private _resumeRefreshing = false;

	/**
	 * PK of the user we've already background-prewarmed the thread list for.
	 * Ensures the prewarm fires once per authenticated session, not on every
	 * user-store emission (profile updates re-emit the same user).
	 */
	private _prewarmedForUserPk: string | null = null;

	constructor(
		private restService: RestService,
		private store: Store<fromApp.AppState>,
		private socketService: SocketService,
		private userService: UserService,
		private pushService: PushService,
		private toastService: ToastService,
		private appLifecycle: AppLifecycleService,
		private router: Router
	) {
		this.initializeUserSubscription();
		this.initializeSocketSubscriptions();
		this._subscriptions.push(
			this.socketService.chatServiceObservable.subscribe((thread) => {
				this.addNewThread(thread);
				this.updateUnreadCount();
			})
		);

		// On foreground (after a sync-worthy background), backfill anything the
		// socket couldn't deliver while suspended: refresh the thread list +
		// unread counts, and nudge the socket back to connected. This runs
		// app-wide, even if the user has never opened the chat area.
		this._subscriptions.push(
			this.appLifecycle.resumed$.subscribe(() => this.handleResume())
		);
	}

	/**
	 * App-foreground sync for the thread list + unread counts. Only runs for an
	 * authenticated user, and never overlaps itself.
	 */
	private handleResume(): void {
		// Always try to restore the live connection so subsequent messages stream in.
		this.socketService.ensureConnected();

		if (!this._user || this._resumeRefreshing) {
			return;
		}

		this._resumeRefreshing = true;
		// refresh=true re-anchors the list to the newest first page and recomputes unread.
		this.getThreads(undefined, true).subscribe({
			next: () => {
				this._resumeRefreshing = false;
			},
			error: (error) => {
				console.error('ChatService - resume refresh failed:', error);
				this._resumeRefreshing = false;
			},
		});
	}

	/**
	 * One-time background fetch of the thread list right after authentication so
	 * the Chat inbox can render from the cached `items` BehaviorSubject the first
	 * time it's opened. Skips if a list is already present (e.g. a resume refresh
	 * already populated it) to avoid a redundant round-trip.
	 */
	private prewarmThreads(): void {
		if (this._totalItems.length) {
			return;
		}
		this.getThreads().subscribe({
			error: (error) => console.error('ChatService - thread prewarm failed:', error),
		});
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach((sub) => sub.unsubscribe());
	}

	private initializeUserSubscription() {
		this._subscriptions.push(
			this.store
				.select('user')
				.pipe(map((e) => e.user))
				.subscribe((user) => {
					if (user) {
						this._user = user;
						console.log('set user on chat Service');
						// Prewarm the thread list in the background as soon as the
						// user is authenticated, even if they never open the Chat
						// tab. This populates the `items` BehaviorSubject so the
						// inbox renders instantly from cache instead of fetching
						// (and showing a spinner) mid-transition on first entry.
						if (this._prewarmedForUserPk !== user.pk) {
							this._prewarmedForUserPk = user.pk;
							this.prewarmThreads();
						}
					} else {
						this._user = null;
						this._prewarmedForUserPk = null;
					}
				})
		);
	}

	private initializeSocketSubscriptions() {
		this._subscriptions.push(
			this.socketService.socketSubject.subscribe((message) => {
				this.handleNewMessage(message);
			})
		);

		this._subscriptions.push(
			this.pushService.pushSubject.subscribe((message: any) => {
				if (message) this.handleNewMessage(message);
			})
		);

		this._subscriptions.push(
			this.socketService.socketReadSubject.subscribe((readEvent) => {
				this.handleReadReceipt(readEvent);
			})
		);
	}

	/*
		This exists to query the full list
		to know if users have engaged prior
		when submitting a new message.
		If they have, we direct them to the chat thread
		to signal they have history and it isn't new.

	*/
	public getAllThreads(): Observable<any> {
		console.log('getAllThreads()');
		return this.restService.get(`${environment.base_api_url}/chat/?page_size=-1`).pipe(
			map((response) => {
				console.log('getAllThreads response:');
				console.log(response);
				if (response?.length) {
					console.log('getAllThreads response.length:', response.length);
					// Correct unread counts for each thread
					const correctedThreads = response.map((thread: any) => ({
						...thread,
						unread_count: this.calculateCorrectUnreadCount(thread)
					}));
					this._totalItems = this.sortThreadsByActivity(correctedThreads);
					this.updateUnreadCount();
					this.items.next(this._totalItems);
				}
				// All threads loaded at once - pagination is complete
				this.threadListCompleteSubject.next(true);
				return response || [];
			})
		);
	}

	public getThreads(next?: string, refresh = false): Observable<any> {
		console.log('getThreads totalItems');
		console.log(this._totalItems);
		
		// Reset pagination complete state on refresh
		if (refresh) {
			this.threadListCompleteSubject.next(false);
		}
		
		return this.restService.get(next || `${environment.base_api_url}/chat/`).pipe(
			map((response) => {
				console.log('getThreads:');
				console.log(response);
				if (response?.results?.length) {
					// Correct unread counts for each thread before processing
					const correctedResults = response.results.map((thread: any) => ({
						...thread,
						unread_count: this.calculateCorrectUnreadCount(thread)
					}));
					
					// Separate archived and non-archived items
					const archivedResults = correctedResults.filter((item: any) => item.isArchived);
					const activeResults = correctedResults.filter((item: any) => !item.isArchived);

					// Accumulate archived items
					archivedResults.forEach((item: any) => {
						this.addToArchivedItems(item);
					});

					let merged = [...this._totalItems, ...activeResults];
					if (refresh) {
						merged = [...activeResults];
						// On refresh, also reset archived items with fresh data
						this._archivedItems = [...archivedResults.map((item: any) => ({ ...item, isArchived: true }))];
						this.archivedItems.next(this._archivedItems);
					}
					this._totalItems = this.sortThreadsByActivity(merged.filter((item, index, self) => index === self.findIndex((t) => t.pk === item.pk)));
					this.updateUnreadCount();
					this.items.next(this._totalItems);
				}
				
				// Update pagination complete state
				this.threadListCompleteSubject.next(!response?.next);
				
				return response;
			})
		);
	}

	/**
	 * Fetch archived threads with pagination support
	 * Uses the ?archived=true filter on the chat endpoint
	 */
	public getArchivedThreads(next?: string, refresh = false): Observable<any> {
		const url = next || `${environment.base_api_url}/chat/?archived=true`;
		return this.restService.get(url).pipe(
			map((response) => {
				console.log('getArchivedThreads:', response);
				if (response?.results?.length) {
					// Correct unread counts before adding to archived items
					const archivedResults = response.results.map((item: any) => ({ 
						...item, 
						isArchived: true,
						unread_count: this.calculateCorrectUnreadCount(item)
					}));
					
					if (refresh) {
						this._archivedItems = [...archivedResults];
					} else {
						// Merge and dedupe
						const merged = [...this._archivedItems, ...archivedResults];
						this._archivedItems = merged.filter(
							(item, index, self) => index === self.findIndex((t) => t.pk === item.pk)
						);
					}
					this._archivedItems = this.sortThreadsByActivity(this._archivedItems);
					this.archivedItems.next(this._archivedItems);
				}
				return response;
			})
		);
	}

	public addNewThread(thread: any): void {
		console.log('addNewThread');
		console.log(thread);
		// Correct unread count for new thread
		const correctedThread = {
			...thread,
			unread_count: this.calculateCorrectUnreadCount(thread)
		};
		this._totalItems.unshift(correctedThread);
		this.items.next(this._totalItems);
	}

	public addNewItem(item: any) {
		this._totalItems = [...this._totalItems, ...item];
		console.log('addNewItem:');
		console.log(item);
		this.items.next(this._totalItems);
	}

	private async handleNewMessage(message: any) {
		const isActiveThread = this.isThreadActive(message.thread);
		if (!this.isMessageFromCurrentUser(message) && !isActiveThread) {
			// @TODO: should consider whether we are in the chat view already, shouldnt show toast or incrememnt
			this.incrementUnreadCount();
			this.toastService.show(
				message.content,
				'top',
				() => {
					console.log('message clicked!');
					this.router.navigate(['/chat/' + message.thread]);
				},
				() => {
					console.log('on toast message dismiss...');
				}
			);
		}

		const threadIndex = this._totalItems.findIndex((t) => t.pk === message.thread);
		if (threadIndex !== -1) {
			console.log('we have a copy of this already!');
			console.log('threadIndex', threadIndex);
			
			// Calculate correct unread count
			// Only increment if message is FROM another user, not from us
			let newUnreadCount = this._totalItems[threadIndex].unread_count;
			if (!this.isMessageFromCurrentUser(message) && !isActiveThread) {
				newUnreadCount = newUnreadCount + 1;
			}
			// If we sent the message, don't change the unread count
			// (it should only reflect messages from others that we haven't read)
			
			const updatedThread = {
				...this._totalItems[threadIndex],
				latest_message: {
					pk: message.pk,
					created: message.created,
					content: message.content,
					data: message.data,
					sender: message.sender,
				},
				unread_count: newUnreadCount,
				// Reset typing state when new message arrives
				isTyping: false,
				typingTimer: undefined,
			};

			// Clear any existing typing timer
			if (this._totalItems[threadIndex].typingTimer) {
				clearTimeout(this._totalItems[threadIndex].typingTimer);
			}

			// Remove thread from current position
			this._totalItems.splice(threadIndex, 1);
			// Add to beginning of array for most recent
			this._totalItems.unshift(updatedThread);

			console.log('this._totalItems[threadIndex]:');
			console.log(this._totalItems[threadIndex]);
			const targetItem = this._totalItems[threadIndex];
			if (targetItem.pk === message.thread) {
				// determine who is other_participant
				// compare against our pk, and request which userId
				if (targetItem.other_participants && typeof targetItem.other_participants[0] === 'string') {
					const isMine = targetItem.other_participants[0] === this.userService.user.pk;
					let targetPk = targetItem.other_participants[0];
					if (isMine) {
						targetPk = message.sender;
					}
					console.log('lets convert this into a user object with avatar and names');
					const response = await this.userService.getUserById(targetPk).toPromise();
					const otherUserDetails = {
						avatar: response.avatar,
						first_name: response.first_name,
						is_staff: response.is_staff,
						last_name: response.last_name,
						pk: response.pk,
					};
					console.log('getUserById response:');
					console.log(response);
					targetItem.other_participants[0] = otherUserDetails;
				}
			}

			this.items.next(this._totalItems);
		} else {
			// @TODO: full refresh of threads?
			// we would normally expect to receive a chat.thread event before
			console.log('We received a message from a user we have no history with!');
			console.log(message);
			// seeing the chat.message to follow
		}
	}

	private handleReadReceipt(readEvent: any) {
		const threadIndex = this._totalItems.findIndex((t) => t.pk === readEvent.thread);
		if (threadIndex !== -1) {
			const thread = this._totalItems[threadIndex];

			if (this.isCurrentUserMessage(readEvent)) {
				const updatedThread = {
					...thread,
					unread_count: 0,
					latest_read: thread.latest_read.map((read) => (read.user === this._user.pk ? { ...read, message: readEvent.message } : read)),
				};
				this._totalItems[threadIndex] = updatedThread;
				this.updateUnreadCount();
				this.items.next(this._totalItems);
			}
		}
	}

	private sortThreadsByActivity(threads: any[]): any[] {
		return threads.sort((a, b) => {
			// if (a.unread_count !== b.unread_count) {
			// 	return b.unread_count - a.unread_count;
			// }
			return new Date(b.latest_message?.created || 0).getTime() - new Date(a.latest_message?.created || 0).getTime();
		});
	}

	public getMessages(threadPk: string, next?: string): Observable<any> {
		return this.restService.get(next || `${environment.base_api_url}/chat/${threadPk}/messages/`);
	}

	private calculateUnreadCount(threads: any[]): number {
		return threads.reduce((total, thread) => total + (thread.unread_count || 0), 0);
	}

	private updateUnreadCount(): void {
		if (!this._totalItems || !this._totalItems.length) {
			return;
		}
		const unreadCount = this.calculateUnreadCount(this._totalItems);
		this.unreadCountSubject.next(unreadCount);
	}

	private incrementUnreadCount(): void {
		const currentCount = this.unreadCountSubject.value;
		this.unreadCountSubject.next(currentCount + 1);
	}

	private isMessageFromCurrentUser(message: any): boolean {
		return message.sender === this._user?.pk;
	}

	private isCurrentUserMessage(readEvent: any): boolean {
		return readEvent.sender === this._user?.pk;
	}

	/**
	 * Calculate if a thread has unread messages FROM other users (not messages we sent)
	 * Backend unread_count includes all unread messages, but we only want to show indicator
	 * for messages FROM others that WE haven't read
	 */
	private calculateCorrectUnreadCount(thread: any): number {
		if (!thread || !thread.latest_message || !this._user) {
			return 0;
		}

		// If the latest message is from us, we need to check if there are actually
		// unread messages from the other user
		const latestMessageFromUs = thread.latest_message.sender === this._user.pk;
		
		// Get our latest read message
		const myLatestRead = Array.isArray(thread.latest_read) 
			? thread.latest_read.find((read: any) => read.user === this._user.pk)
			: null;

		// If we haven't read anything yet and latest message is from someone else, show unread
		if (!myLatestRead) {
			return latestMessageFromUs ? 0 : thread.unread_count || 0;
		}

		// If we've read the latest message, there are no unread messages
		if (myLatestRead.message === thread.latest_message.pk) {
			return 0;
		}

		// If the latest message is from us, don't show unread indicator
		// (even if backend says there are unread messages, they're OUR messages that
		// the other person hasn't read, not their messages that we haven't read)
		if (latestMessageFromUs) {
			// We need to check if there are messages from them that we haven't read
			// Since we don't have all message data here, we can't be 100% certain,
			// but if the latest message is from us and we've read it (we sent it),
			// then the backend unread_count is likely counting OUR unread-by-them messages
			// which we should NOT show an indicator for
			return 0;
		}

		// Latest message is from someone else and we haven't read it
		return thread.unread_count || 0;
	}

	public setActiveThread(threadPk: string | null): void {
		this.activeThreadSubject.next(threadPk);
	}

	public isThreadActive(threadPk: string | null): boolean {
		const activeThread = this.activeThreadSubject.value;
		return !!threadPk && !!activeThread && threadPk === activeThread;
	}

	public findUserThreadPk(userPk: string): string | null {
		const foundThread = this._totalItems.find((thread) => thread.other_participants.some((participant) => participant.pk === userPk));
		return foundThread ? foundThread.pk : null;
	}

	public clearThreads(): void {
		this._totalItems = [];
		this._archivedItems = [];
		this._user = null;
		this.unreadCountSubject.next(0);
		this.items.next([]);
		this.archivedItems.next([]);
		this.threadListCompleteSubject.next(false);
		this.activeThreadSubject.next(null);
	}

	public getUserThreadById(threadPk: string): Observable<any> {
		return this.restService.get(`${environment.base_api_url}/chat/${threadPk}/`).pipe(
			map((thread) => {
				// Correct unread count before returning
				return {
					...thread,
					unread_count: this.calculateCorrectUnreadCount(thread)
				};
			})
		);
	}

	public archiveThread(threadPk: string): Observable<any> {
		return this.restService.post(`${environment.base_api_url}/chat/${threadPk}/archive/`, { id: threadPk }).pipe(
			map((response) => {
				// Find the thread in _totalItems
				const threadIndex = this._totalItems.findIndex((t) => t.pk === threadPk);
				if (threadIndex !== -1) {
					const archivedThread = { ...this._totalItems[threadIndex], isArchived: true };
					// Remove from active items
					this._totalItems.splice(threadIndex, 1);
					// Add to archived items
					this._archivedItems.push(archivedThread);
					// Update observables
					this.items.next(this._totalItems);
					this.archivedItems.next(this._archivedItems);
					this.updateUnreadCount();
				}
				return response;
			})
		);
	}

	public unarchiveThread(threadPk: string): Observable<any> {
		return this.restService.post(`${environment.base_api_url}/chat/${threadPk}/unarchive/`, { id: threadPk }).pipe(
			map((response) => {
				// Find the thread in _archivedItems
				const threadIndex = this._archivedItems.findIndex((t) => t.pk === threadPk);
				if (threadIndex !== -1) {
					const unarchivedThread = { ...this._archivedItems[threadIndex], isArchived: false };
					// Remove from archived items
					this._archivedItems.splice(threadIndex, 1);
					// Add back to active items and sort
					this._totalItems.push(unarchivedThread);
					this._totalItems = this.sortThreadsByActivity(this._totalItems);
					// Update observables
					this.items.next(this._totalItems);
					this.archivedItems.next(this._archivedItems);
					this.updateUnreadCount();
				}
				return response;
			})
		);
	}

	public addToArchivedItems(item: any): void {
		const exists = this._archivedItems.some((t) => t.pk === item.pk);
		if (!exists) {
			this._archivedItems.push({ ...item, isArchived: true });
			this.archivedItems.next(this._archivedItems);
		}
	}

	public clearArchivedItems(): void {
		this._archivedItems = [];
		this.archivedItems.next([]);
	}
}
